import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toggleJob, deleteJob, type CronJob } from '../../api/cron';
import { useSnackbar } from '../../context/SnackbarContext';
import { useConfirm } from '../../context/ConfirmDialogContext';

// ── Human expression summary (minimal, browser-side) ──────────────────────

function exprSummary(expr: string): string {
  const p = expr.split(/\s+/);
  if (p.length !== 5) return expr;
  const [min, hr, , , dow] = p;
  const parts: string[] = [];
  if (min === '*') parts.push('every minute');
  else if (min.startsWith('*/')) parts.push(`every ${min.slice(2)} min`);
  else parts.push(`at :${min.padStart(2, '0')}`);
  if (hr !== '*') {
    if (hr.startsWith('*/')) parts.push(`every ${hr.slice(2)}h`);
    else parts.push(`at ${hr.padStart(2, '0')}:00`);
  }
  if (dow === '1-5') parts.push('weekdays');
  else if (dow === '6,0') parts.push('weekends');
  else if (dow !== '*') parts.push(['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][Number(dow)] ?? dow);
  return parts.join(', ');
}

function fmtNextRun(nextRun: string | null): string {
  if (!nextRun) return '—';
  return new Date(nextRun).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// ── Row ────────────────────────────────────────────────────────────────────

function JobRow({ job }: { job: CronJob }) {
  const qc = useQueryClient();
  const showSnackbar = useSnackbar();
  const confirm = useConfirm();

  const toggleMut = useMutation({
    mutationFn: () => toggleJob(job.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cron', 'jobs'] }); },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteJob(job.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cron', 'jobs'] });
      showSnackbar('Cron job deleted', 'success');
    },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  async function handleDelete() {
    const ok = await confirm(
      `Delete "${job.source === 'webadmin' ? job.name : job.command}"? This cannot be undone.`,
      { title: 'Delete cron job', confirmLabel: 'Delete', danger: true },
    );
    if (ok) deleteMut.mutate();
  }

  const isSystem = job.source === 'system';
  const systemTooltip = 'System jobs cannot be managed here';

  return (
    <TableRow hover>
      {/* Status dot */}
      <TableCell sx={{ width: 20, pr: 0 }}>
        {isSystem ? (
          <Tooltip title="Read-only system job">
            <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          </Tooltip>
        ) : (
          <Box
            sx={{
              width: 10, height: 10, borderRadius: '50%',
              bgcolor: job.active ? 'success.main' : 'error.main',
              display: 'inline-block',
            }}
          />
        )}
      </TableCell>

      {/* Name / command */}
      <TableCell>
        {job.source === 'webadmin' ? (
          <>
            <Typography variant="body2" fontWeight={600}>{job.name}</Typography>
            {job.description && <Typography variant="caption" color="text.secondary">{job.description}</Typography>}
          </>
        ) : (
          <Typography variant="caption" fontFamily="monospace" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {job.command}
          </Typography>
        )}
      </TableCell>

      {/* Schedule summary */}
      <TableCell>
        <Typography variant="caption">{exprSummary(job.expression)}</Typography>
      </TableCell>

      {/* Expression badge */}
      <TableCell>
        <Chip label={job.expression} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
      </TableCell>

      {/* Next run */}
      <TableCell>
        <Typography variant="caption" fontFamily="monospace">{fmtNextRun(null)}</Typography>
      </TableCell>

      {/* User */}
      <TableCell>
        <Typography variant="caption">{job.user}</Typography>
      </TableCell>

      {/* Source chip */}
      <TableCell>
        {job.source === 'webadmin' ? (
          <Chip label="WEBADMIN" size="small" color="primary" variant="outlined" />
        ) : (
          <Chip label="SYSTEM" size="small" variant="outlined" sx={{ color: 'text.disabled' }} />
        )}
      </TableCell>

      {/* Actions */}
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title={isSystem ? systemTooltip : (job.active ? 'Pause' : 'Resume')}>
            <span>
              <IconButton
                size="small"
                disabled={isSystem || toggleMut.isPending}
                onClick={() => toggleMut.mutate()}
              >
                {toggleMut.isPending ? <CircularProgress size={14} /> : job.active ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isSystem ? systemTooltip : 'Delete'}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={isSystem || deleteMut.isPending}
                onClick={() => void handleDelete()}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

// ── Filter bar + table ─────────────────────────────────────────────────────

type Filter = 'all' | 'webadmin' | 'system' | 'active' | 'paused';

interface Props {
  jobs: CronJob[];
  loading: boolean;
}

export function JobTable({ jobs, loading }: Props) {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const visible = jobs.filter((j) => {
    if (filter === 'webadmin' && j.source !== 'webadmin') return false;
    if (filter === 'system'   && j.source !== 'system')   return false;
    if (filter === 'active'   && !j.active)                return false;
    if (filter === 'paused'   && j.active)                 return false;
    if (search) {
      const q = search.toLowerCase();
      const name = j.source === 'webadmin' ? j.name.toLowerCase() : '';
      if (!name.includes(q) && !j.command.toLowerCase().includes(q) && !j.expression.includes(q)) return false;
    }
    return true;
  });

  return (
    <Box>
      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Filter</InputLabel>
          <Select value={filter} label="Filter" onChange={(e) => setFilter(e.target.value as Filter)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="webadmin">WebAdmin</MenuItem>
            <MenuItem value="system">System</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="paused">Paused</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search by name or command…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
        />
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 20 }} />
              <TableCell>Name / Command</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Expression</TableCell>
              <TableCell>Next run</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Source</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>No jobs match the current filter.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              visible.map((job) => <JobRow key={job.id} job={job} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
